import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DeleteMyAccount, JoinSettingsCard, WaysInCard } from './sign-in-with';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { BUYABLE, firmPrice, PLAN_PRICES, type BuyablePlan, type Interval, type SelfServePlan } from '@fsp/core';
import { api, type Customer, type Me } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell, selfServe, ThirdPane } from '../layout';
import { fillFields, useRegistry } from './registry';
import { TeamCard } from './team';
import { FirmLinkCard } from './firm';
import { ContractCard } from './contract';

const CUSTOMER_FIELDS = ['name', 'companyCode', 'vatCode', 'address', 'email', 'phone', 'contactPerson', 'bankName', 'iban', 'swift', 'notes'] as const;

export function CustomersPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Customer | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const list = useQuery({ queryKey: ['customers'], queryFn: () => api.get<{ customers: Customer[] }>('api/v1/customers') });
  const save = useMutation({
    mutationFn: (values: Record<string, string>) => editing ? api.put(`api/v1/customers/${editing.id}`, values) : api.post('api/v1/customers', values),
    onSuccess: async () => {
      setNotice(editing ? t('Kliento duomenys atnaujinti.') : t('Klientas pridėtas.'));
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`api/v1/customers/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    save.mutate(Object.fromEntries(CUSTOMER_FIELDS.map((f) => [f, String(data.get(f) ?? '')])));
    if (!editing) event.currentTarget.reset();
  };
  const customers = list.data?.customers ?? [];
  const v = (field: typeof CUSTOMER_FIELDS[number]) => editing?.[field] ?? '';
  const formRef = useRef<HTMLFormElement>(null);
  const registry = useRegistry((c) => fillFields(formRef.current, { name: 'name', code: 'companyCode', vatCode: 'vatCode', address: 'address' }, c));
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Kontaktai')} title={t('Klientai')} text={t('Čia automatiškai rodomi sąskaitose išsaugoti klientai. Taip pat galite pridėti klientą rankiniu būdu.')} />}>
      {notice ? <Alert kind="success">{notice}</Alert> : null}
      <div className="customer-layout">
        <section className="panel-card">
          <div className="card-head"><div><p className="eyebrow">{t('Klientų bazė')}</p><h2>{t('Sąrašas')}</h2></div><span className="file-count">{customers.length} {t('klient.')}</span></div>
          <div className="customer-list">
            {list.isError ? <Alert kind="error">{errorText(t, list.error)}</Alert> : null}
            {list.isSuccess && !customers.length ? <div className="empty-state compact"><h3>{t('Klientų dar nėra')}</h3><p>{t('Pridėkite pirmąjį klientą dešinėje arba kurdami sąskaitą.')}</p></div> : null}
            {customers.map((c) => (
              <article className="customer-row" key={c.id}>
                <div className="customer-avatar">{c.name.slice(0, 2).toUpperCase()}</div>
                <div className="customer-main">
                  <h3>{c.name}</h3>
                  <p>{c.companyCode || t('Įmonės kodas nenurodytas')}{c.vatCode ? ` · ${c.vatCode}` : ''}</p>
                  <small>{c.email || t('El. paštas nenurodytas')}{c.address ? ` · ${c.address}` : ''}</small>
                </div>
                <div className="customer-actions">
                  <button type="button" className="button button--ghost button--small" onClick={() => { setEditing(c); setNotice(null); }}>{t('Redaguoti')}</button>
                  <button type="button" className="delete-x" aria-label={`${t('Pašalinti klientą')} ${c.name}`}
                    onClick={() => { if (confirm(t('Pašalinti klientą „{name}“? Išrašytos sąskaitos išliks.', { name: c.name }))) remove.mutate(c.id); }}>×</button>
                </div>
              </article>
            ))}
          </div>
        </section>
        <ThirdPane className="panel-card customer-add" label={t('Pridėti klientą')} open={Boolean(editing)}>
          <p className="eyebrow">{editing ? t('Kliento redagavimas') : t('Naujas klientas')}</p>
          <h2>{editing ? t('Redaguoti klientą') : t('Pridėti rankiniu būdu')}</h2>
          {save.isError ? <Alert kind="error">{errorText(t, save.error)}</Alert> : null}
          <form key={editing?.id ?? 'new'} onSubmit={submit} ref={formRef}>
            <label>{t('Pavadinimas')}<input name="name" defaultValue={v('name')} required placeholder={t('Įmonė arba asmuo')} {...registry.nameInput} /></label>
            <div className="form-grid">
              <label>{t('Įmonės kodas')}<span className="input-with-button"><input name="companyCode" defaultValue={v('companyCode')} />{registry.button(() => (formRef.current?.elements.namedItem('companyCode') as HTMLInputElement | null)?.value ?? '')}</span></label>
              <label>{t('PVM kodas')}<input name="vatCode" defaultValue={v('vatCode')} /></label>
            </div>
            <label>{t('Adresas')}<textarea name="address" rows={2} defaultValue={v('address')} /></label>
            {registry.extras}
            <p className="form-section">{t('Kontaktai')}</p>
            <div className="form-grid">
              <label>{t('El. paštas')}<input type="email" name="email" defaultValue={v('email')} /></label>
              <label>{t('Telefonas')}<input type="tel" name="phone" defaultValue={v('phone')} /></label>
              <label className="span-2">{t('Kontaktinis asmuo')}<input name="contactPerson" defaultValue={v('contactPerson')} /></label>
            </div>
            <p className="form-section">{t('Banko rekvizitai')}</p>
            <div className="form-grid">
              <label>{t('Bankas')}<input name="bankName" defaultValue={v('bankName')} /></label>
              <label>SWIFT / BIC<input name="swift" defaultValue={v('swift')} /></label>
              <label className="span-2">IBAN<input name="iban" defaultValue={v('iban')} /></label>
            </div>
            <label>{t('Pastabos')}<textarea name="notes" rows={2} defaultValue={v('notes')} /></label>
            <div className="form-actions">
              <button className="button button--primary" disabled={save.isPending}>{editing ? t('Išsaugoti pakeitimus') : t('Pridėti klientą')}</button>
              {editing ? <button type="button" className="button button--ghost" onClick={() => setEditing(null)}>{t('Atšaukti')}</button> : null}
            </div>
          </form>
        </ThirdPane>
      </div>
    </PanelShell>
  );
}

type AccountTab = 'profile' | 'security' | 'team' | 'contract' | 'plan' | 'data';
const ACCOUNT_TABS: AccountTab[] = ['profile', 'security', 'team', 'contract', 'plan', 'data'];

/** Nustatymai in tabs; the open tab is kept in the address (#security) so it survives a reload. */
export function AccountPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const [tab, setTab] = useState<AccountTab>(() => {
    const hash = window.location.hash.slice(1) as AccountTab;
    return ACCOUNT_TABS.includes(hash) ? hash : new URLSearchParams(window.location.search).has('billing') ? 'plan' : 'profile';
  });
  // The menu's Vartotojai / Nustatymai tabs open this page with or without #team.
  const hash = useRouterState({ select: (s) => s.location.hash });
  const firstHash = useRef(true);
  useEffect(() => {
    if (firstHash.current) { firstHash.current = false; return; }
    const key = hash as AccountTab;
    setTab(ACCOUNT_TABS.includes(key) ? key : 'profile');
  }, [hash]);
  const navigate = useNavigate();
  const chooseTab = (key: AccountTab) => { setTab(key); setMessage(null); void navigate({ to: '/account', hash: key, replace: true }); };
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const change = useMutation({
    mutationFn: (v: { currentPassword: string; newPassword: string }) => api.post('api/v1/auth/password', v),
    onSuccess: () => setMessage({ kind: 'success', text: t('Slaptažodis pakeistas. Kituose įrenginiuose reikės prisijungti iš naujo.') }),
    onError: (error) => setMessage({ kind: 'error', text: errorText(t, error) }),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    if (data.get('newPassword') !== data.get('confirm')) { setMessage({ kind: 'error', text: t('Slaptažodžiai nesutampa.') }); return; }
    change.mutate({ currentPassword: String(data.get('currentPassword')), newPassword: String(data.get('newPassword')) });
    event.currentTarget.reset();
  };
  const tabs: [AccountTab, string][] = [['profile', 'Profilis'], ['security', 'Saugumas'], ['team', 'Komanda'], ['contract', 'Sutartis'], ['plan', 'Planas'], ['data', 'Duomenys ir paskyra']];
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Paskyra')} title={t('Nustatymai')} text={t('Profilis, slaptažodis, planas ir įmonės duomenų archyvas.')} />}>
      <nav className="project-tabs page-tabs" role="tablist">
        {tabs.map(([key, label]) => <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? 'active' : ''} onClick={() => chooseTab(key)}>{t(label)}</button>)}
      </nav>
      {message ? <Alert kind={message.kind}>{message.text}</Alert> : null}
      <div className="account-tab">
        {tab === 'profile' ? <section className="panel-card">
          <div className="account-head"><div className="customer-avatar">{me.user.name.slice(0, 2).toUpperCase()}</div>
            <div><p className="eyebrow">{t('Profilis')}</p><h2>{me.user.name}</h2><p className="muted">{me.user.email}</p></div></div>
          <p className="muted account-note">{t('Prisijungimo el. paštą gali pakeisti administratorius.')}</p>
          <p className="muted account-note">{t('Įmonės rekvizitai ir logotipas — puslapyje „Įmonė“.')} <Link to="/company" className="text-link">{t('Atidaryti')}</Link></p>
        </section> : null}
        {tab === 'security' ? <section className="panel-card">
          <p className="eyebrow">{t('Saugumas')}</p><h2>{t('Keisti slaptažodį')}</h2>
          <form onSubmit={submit}>
            <input type="text" name="username" value={me.user.email} autoComplete="username" hidden readOnly />
            <label>{t('Dabartinis slaptažodis')}<input type="password" name="currentPassword" autoComplete="current-password" required /></label>
            <label>{t('Naujas slaptažodis')}<input type="password" name="newPassword" minLength={8} autoComplete="new-password" required /></label>
            <label>{t('Pakartokite naują slaptažodį')}<input type="password" name="confirm" minLength={8} autoComplete="new-password" required /></label>
            <div className="form-actions"><button className="button button--primary" disabled={change.isPending}>{t('Keisti slaptažodį')}</button></div>
          </form>
        </section> : null}
        {tab === 'security' ? <WaysInCard me={me} /> : null}
        {tab === 'security' ? <AdminAccess me={me} /> : null}
        {tab === 'team' ? <TeamCard me={me} /> : null}
        {tab === 'team' ? <JoinSettingsCard me={me} /> : null}
        {tab === 'team' && me.subscription?.plan !== 'firm' ? <FirmLinkCard me={me} /> : null}
        {tab === 'contract' ? <ContractCard me={me} /> : null}
        {tab === 'plan' ? <PlanCard me={me} /> : null}
        {tab === 'data' ? <DataAndClosing me={me} /> : null}
        {tab === 'data' ? <DeleteMyAccount me={me} /> : null}
      </div>
    </PanelShell>
  );
}

/** B6: the company's archive (ZIP) and, for the owner, closing the account. */
function DataAndClosing({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const close = useMutation({
    mutationFn: (password: string) => api.post('api/v1/account/close', { password, confirm: true }),
    onSuccess: async () => { queryClient.clear(); await navigate({ to: '/login', search: { closed: 1 } }); },
  });
  return (
    <section className="panel-card account-data">
      <p className="eyebrow">{t('Duomenys ir paskyra')}</p><h2>{t('Visų duomenų archyvas')}</h2>
      <p className="muted">{t('ZIP failas: visos išrašytos sąskaitos PDF formatu, gautų sąskaitų ir sutarčių failai, sąskaitų ir klientų sąrašai (CSV). Paruošimas gali užtrukti minutę.')}</p>
      <a className="button button--ghost" href="api/v1/account/archive" download>{t('Atsisiųsti archyvą')}</a>
      {me.company.role === 'owner' && !me.viewAsAdmin ? (
        <details className="account-close" open={open} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
          <summary>{t('Uždaryti paskyrą')}</summary>
          <p>{t('Uždarius paskyrą niekas nebegalės prisijungti. Naudotojai, užrašai, užduotys, kalendoriaus ryšys, klientų sąrašas ir el. pašto nustatymai ištrinami iš karto. Sąskaitos ir jų failai saugomi pagal įstatymą ir ištrinami vėliau. Prieš uždarydami atsisiųskite archyvą.')}</p>
          {close.isError ? <Alert kind="error">{errorText(t, close.error)}</Alert> : null}
          <form onSubmit={(e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            if (d.get('agree')) close.mutate(String(d.get('password')));
          }}>
            <label className="remember-device"><input type="checkbox" name="agree" required /><span>{t('Suprantu, kad paskyros atkurti nebus galima.')}</span></label>
            <label>{t('Slaptažodis')}<input type="password" name="password" autoComplete="current-password" required /></label>
            <div className="form-actions"><button className="button button--danger" disabled={close.isPending}>{t('Uždaryti paskyrą')}</button></div>
          </form>
        </details>
      ) : null}
    </section>
  );
}

const PLAN_NAMES: Record<string, string> = { free: 'Nemokamas', basic: 'Basic', premium: 'Premium', full: 'Pilna apskaita', firm: 'Firm', iv: 'Individuali veikla' };

/**
 * B1: the company's plan and where it stands (trial, paid until, free, or Premium through its accounting firm).
 * The owner picks Basic, Premium or Firm (Individuali veikla for a person working alone), monthly or yearly (a year
 * is ten months), and pays by card through Stripe; with a running subscription the plan is changed in place (the
 * difference is prorated). During the trial another plan can be tried without a card. Pilna apskaita is by agreement.
 */
function PlanCard({ me }: { me: Me }) {
  const { t } = useI18n();
  const s = me.subscription;
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // A person working on their own picks Individuali veikla (or Premium); a company Basic or Premium.
  const iv = me.company.kind === 'iv';
  const choices = BUYABLE.filter((p) => (iv ? p === 'iv' || p === 'premium' : p !== 'iv'));
  const [plan, setPlan] = useState<BuyablePlan>((choices as readonly string[]).includes(s?.plan ?? '') ? s!.plan as BuyablePlan : iv ? 'iv' : 'premium');
  // Firm is billed for its active clients (at least five).
  const clients = useQuery({ queryKey: ['firm-clients'], queryFn: () => api.get<{ clients: { status: string }[] }>('api/v1/firm/clients'), enabled: s?.plan === 'firm' });
  const clientCount = clients.data?.clients.filter((c) => c.status === 'active').length ?? 0;
  const [period, setPeriod] = useState<Interval>('month');
  const [changed, setChanged] = useState(false);
  const queryClient = useQueryClient();
  if (!s) return null;
  const price = s.priceCents ?? s.listPriceCents;
  const day = (iso: string | null) => (iso ? iso.slice(0, 10) : '');
  const status = s.state === 'free' ? t('Nemokamai')
    : s.state === 'paid' ? t('Apmokėta iki {date}', { date: day(s.paidUntil) })
    : s.state === 'trial' ? t('Bandomasis Premium laikotarpis iki {date}', { date: day(s.trialEndsAt) })
    : s.state === 'expired' ? t('Baigėsi — dabar Nemokamas planas') : '—';
  const billing = me.billing;
  const live = Boolean(billing?.status && ['active', 'trialing', 'past_due', 'unpaid', 'incomplete'].includes(billing.status));
  const canPay = selfServe(me) && me.company.role === 'owner' && !me.viewAsAdmin;
  const needsContract = Boolean(me.contract?.required) && !['uploaded', 'accepted'].includes(me.contract?.status ?? '');
  const done = new URLSearchParams(window.location.search).get('billing') === 'done';
  // Stripe's own pages: checkout for a new subscription, the portal for card changes and cancelling.
  const go = async (path: 'checkout' | 'portal') => {
    setBusy(true); setError(null);
    try { window.location.assign((await api.post<{ url: string }>(`api/v1/billing/${path}`, path === 'checkout' ? { plan, interval: period } : {})).url); } catch (e) { setError(e); setBusy(false); }
  };
  const change = async () => {
    if (!confirm(t('Pakeisti planą į {plan} ({period})? Skirtumas bus perskaičiuotas likusiam laikotarpiui.', { plan: PLAN_NAMES[plan]!, period: period === 'year' ? t('metams') : t('mėnesiui') }))) return;
    setBusy(true); setError(null);
    try { await api.post('api/v1/billing/change', { plan, interval: period }); setChanged(true); await queryClient.invalidateQueries({ queryKey: ['me'] }); } catch (e) { setError(e); }
    setBusy(false);
  };
  const priceOf = (p: BuyablePlan, i: Interval) => {
    const amount = p === 'firm' ? firmPrice(clientCount, i) : PLAN_PRICES[p as SelfServePlan][i];
    return i === 'year' ? t('{price} / metus + PVM', { price: money(amount) }) : t('{price} / mėn. + PVM', { price: money(amount) });
  };
  const tryPlan = async () => {
    setBusy(true); setError(null);
    try { await api.post('api/v1/billing/trial-plan', { plan }); setChanged(true); await queryClient.invalidateQueries({ queryKey: ['me'] }); } catch (e) { setError(e); }
    setBusy(false);
  };
  const onTrial = s.state === 'trial' && !live;
  return (
    <section className="panel-card account-data">
      <p className="eyebrow">{t('Planas')}</p><h2>{t(PLAN_NAMES[s.plan]!)}{s.state === 'trial' ? <small className="muted"> · {t('bandomasis laikotarpis: Premium')}</small> : null}</h2>
      {done ? <Alert kind="success">{t('Ačiū! Prenumerata užregistruota — būsena atsinaujins per kelias sekundes.')}</Alert> : null}
      {changed ? <Alert kind="success">{t('Planas pakeistas.')}</Alert> : null}
      {error ? <Alert kind="error">{errorText(t, error)}</Alert> : null}
      {billing?.status === 'past_due' || billing?.status === 'unpaid' ? <Alert kind="error">{t('Mokėjimas nepavyko. Atnaujinkite kortelę prenumeratos valdyme.')}</Alert> : null}
      <p className={s.state === 'expired' ? 'text-late' : 'muted'}>{status}</p>
      {s.state !== 'free' && s.state !== 'trial' && s.state !== 'expired' ? <p className="muted">{price === null ? t('Kaina pagal susitarimą') : t('{price} / mėn. + PVM', { price: money(price) })}</p> : null}
      {s.coveredByFirm ? <p className="muted">{t('Premium apmoka jūsų buhalterinė įmonė (Įmonė → Vartotojai → „Buhalterinė įmonė“).')}</p> : null}
      {canPay && live && billing?.status === 'trialing' ? <p className="muted">{t('Prenumerata aktyvi: pirmas mokėjimas bus nuskaitytas pasibaigus bandomajam laikotarpiui.')}</p> : null}
      {canPay && !s.coveredByFirm ? (
        <div className="plan-choice">
          <div className="plan-choice__plans" role="radiogroup" aria-label={t('Planas')}>
            {choices.map((p) => (
              <label key={p} className={`plan-choice__plan${plan === p ? ' is-selected' : ''}`}>
                <input type="radio" name="plan" value={p} checked={plan === p} onChange={() => setPlan(p)} />
                <strong>{t(PLAN_NAMES[p]!)}</strong><span>{priceOf(p, period)}</span>
                <small className="muted">{p === 'basic' ? t('Sąskaitos, pasiūlymai, sutartys ir darbai; 1 vartotojas ir buhalteris (peržiūra).')
                  : p === 'iv' ? t('Pajamų ir išlaidų žurnalas, GPM, VSD ir PSD skaičiavimas, sąskaitos.')
                  : p === 'firm' ? t('Buhalterinėms įmonėms: visi klientai vienoje panelėje, kiekvienas su Premium. 30 € už klientą (21–50-as — 27 €, nuo 51-o — 24 €), mažiausiai 5; dabar klientų: {n}.', { n: clientCount })
                  : t('Viskas: bankas, kasa, mokesčiai, atlyginimai, sąskaitos el. paštu su AI; iki 5 vartotojų.')}</small>
              </label>
            ))}
          </div>
          <div className="calendar-view-switch" role="group" aria-label={t('Mokėjimo laikotarpis')}>
            <a href="#month" className={period === 'month' ? 'active' : undefined} aria-pressed={period === 'month'} onClick={(e) => { e.preventDefault(); setPeriod('month'); }}>{t('Kas mėnesį')}</a>
            <a href="#year" className={period === 'year' ? 'active' : undefined} aria-pressed={period === 'year'} onClick={(e) => { e.preventDefault(); setPeriod('year'); }}>{t('Metams (2 mėn. dovanų)')}</a>
          </div>
        </div>
      ) : null}
      <div className="form-actions">
        {canPay && !live && !s.coveredByFirm ? <button type="button" className="button button--primary" disabled={busy || needsContract} onClick={() => void go('checkout')}>{t('Užsisakyti kortele')}</button> : null}
        {canPay && onTrial && plan !== s.plan && !s.coveredByFirm ? <button type="button" className="button button--ghost" disabled={busy} onClick={() => void tryPlan()}>{t('Išbandyti nemokamai iki bandymo pabaigos')}</button> : null}
        {canPay && live ? <button type="button" className="button button--primary" disabled={busy} onClick={() => void change()}>{t('Pakeisti planą')}</button> : null}
        {canPay && (live || billing?.status) ? <button type="button" className="button button--ghost" disabled={busy} onClick={() => void go('portal')}>{t('Tvarkyti prenumeratą ir sąskaitas')}</button> : null}
        <Link to="/shop" className="button button--ghost button--small">{t('Palyginti planus')}</Link>
        <Link to="/support" className="button button--ghost button--small">{t('Klausimai dėl plano')}</Link>
      </div>
      {canPay && !live && needsContract ? <p className="muted account-note">{t('Prieš užsisakydami pasirašykite sutartį (skiltis „Sutartis“).')}</p> : null}
      {canPay ? <p className="muted account-note">{t('Mokama saugiai per Stripe. Kortelės duomenys pas mus nesaugomi. Nuolaidos kodą galite įvesti mokėjimo puslapyje.')}</p> : null}
    </section>
  );
}

interface AccessRequest { id: number; reason: string; state: 'pending' | 'approved' | 'declined' | 'used' | 'revoked' | 'expired'; requestedAt: string; respondedAt: string | null; approvedUntil: string | null; usedAt: string | null }

/** B3a: the administrator's access requests to this company — decide, withdraw, end a running view. */
function AdminAccess({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const data = useQuery({ queryKey: ['view-requests'], queryFn: () => api.get<{ requests: AccessRequest[]; viewing: string | null }>('api/v1/view-requests') });
  const act = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['view-requests'] }) });
  const owner = me.company.role === 'owner' && !me.viewAsAdmin;
  const d = data.data;
  return (
    <section className="panel-card account-access">
      <p className="eyebrow">{t('Privatumas')}</p><h2>{t('Administratoriaus prieiga')}</h2>
      <p className="muted">{t('FakturaSmart Pro administratorius jūsų paskyros nemato. Jei reikia pagalbos, jis gali paprašyti leidimo vieną valandą peržiūrėti paskyrą (tik skaityti). Apie prašymą gausite el. laišką; sprendžiate jūs.')}</p>
      {act.isError ? <Alert kind="error">{errorText(t, act.error)}</Alert> : null}
      {d?.viewing ? (
        <div className="access-live">
          <strong>{t('Administratorius dabar peržiūri paskyrą (iki {time}).', { time: new Date(d.viewing).toLocaleTimeString() })}</strong>
          {owner ? <button type="button" className="button button--danger button--small" onClick={() => { const r = d.requests.find((x) => x.state === 'used'); if (r) act.mutate(() => api.post(`api/v1/view-requests/${r.id}/revoke`)); }}>{t('Nutraukti peržiūrą')}</button> : null}
        </div>
      ) : null}
      {d && !d.requests.length ? <p className="muted">{t('Prašymų nebuvo.')}</p> : null}
      <ul className="access-list">
        {d?.requests.map((r) => (
          <li key={r.id} className={`is-${r.state}`}>
            <div>
              <strong>{r.reason}</strong>
              <small>{new Date(r.requestedAt).toLocaleString()} · {t(ACCESS_STATES[r.state])}{r.state === 'approved' && r.approvedUntil ? ` · ${t('iki')} ${new Date(r.approvedUntil).toLocaleString()}` : ''}</small>
            </div>
            {owner ? <span className="row-actions">
              {r.state === 'pending' ? <>
                <button type="button" className="button button--primary button--small" onClick={() => act.mutate(() => api.post(`api/v1/view-requests/${r.id}/decision`, { decision: 'approve' }))}>{t('Leisti')}</button>
                <button type="button" className="button button--ghost button--small" onClick={() => act.mutate(() => api.post(`api/v1/view-requests/${r.id}/decision`, { decision: 'decline' }))}>{t('Atsisakyti')}</button>
              </> : null}
              {r.state === 'approved' ? <button type="button" className="button button--ghost button--small" onClick={() => act.mutate(() => api.post(`api/v1/view-requests/${r.id}/revoke`))}>{t('Atšaukti leidimą')}</button> : null}
            </span> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

const ACCESS_STATES: Record<AccessRequest['state'], string> = {
  pending: 'Laukia jūsų sprendimo', approved: 'Leista (galioja 24 val.)', declined: 'Atsisakyta', used: 'Panaudota', revoked: 'Atšaukta', expired: 'Nebegalioja',
};
